// todo split this file for lotto and sportsbook
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';

import {stripRoutingPrefix, stripPathLang} from '#core/utils/url-utils.js';
import {formatTranslationString} from '#core/utils/utils.js';

import {ApplicationConfig} from '#core/config/application-config.js';

import {SEO} from './index.js';

const StringUtils = MojitoCore.Base.StringUtils;
const AbstractHeadMetaFactory = MojitoPresentation.Components.AbstractHeadMetaFactory;
const NavigationPathBuilder = MojitoPresentation.Base.Navigation.NavigationPathBuilder;

export class BaseHeadMetaFactory extends AbstractHeadMetaFactory {
    constructor(l10n) {
        super();
        this.l10n = l10n;
        this.routeResolver = null; // will be overridden outside (temp workaround for MOJ-11146)
    }

    setParameters(context) {
        this.routeResolver = context.routeResolver;
    }

    getTitle(data) {
        return this.getTranslation(data, 'title');
    }

    getKeywords(data) {
        return this.getTranslation(data, 'keywords');
    }

    getDescription(data) {
        return this.getTranslation(data, 'description');
    }

    // eslint-disable-next-line no-unused-vars
    getCanonical(data) {
        // todo get pahtLang from Portal
        const sportsbookRootURL = stripPathLang(ApplicationConfig.initParams.routingPrefix);
        return new URL(
            `${sportsbookRootURL === '/' ? '' : sportsbookRootURL}${this.getRelativeCanonical()}`,
            window.location.origin
        ).toString();
    }

    // Returns the canonical URL without window.location.origin
    getRelativeCanonical() {
        return stripRoutingPrefix(ApplicationConfig.initParams.routingPrefix, window.location.pathname);
    }

    // Returns array of meta elements
    getMeta(custom = []) {
        return SEO.meta.all.concat(custom);
    }

    // Returns array of image objects for the Open Graph protocol
    getOpenGraphImages() {
        return SEO.openGraphImages;
    }

    getTranslation(data, part) {
        let phrase;
        const key = this.getKey(data, part);
        if (key) {
            const str = StringUtils.resolveString(key, this.l10n, true);
            phrase = data.localizedParams ? formatTranslationString(str, ...data.localizedParams.values()) : str;
        }
        if (phrase) return phrase;

        // fallback to moj default
        const result = StringUtils.resolveString(data[part], this.l10n, true);
        return data.localizedParams ? formatTranslationString(result, ...data.localizedParams.values()) : result;
    }

    // eslint-disable-next-line no-unused-vars
    getKey(data, part) {
        return '';
    }
}

export class HomePageHeadMetaFactory extends BaseHeadMetaFactory {
    getKey(data, part) {
        return data[part];
    }
    getRelativeCanonical() {
        return '';
    }
    getMeta() {
        return super.getMeta(SEO.meta.home);
    }
}

export class BadRouteHeadMetaFactory extends BaseHeadMetaFactory {
    getCanonical() {
        return null;
    }
    getRelativeCanonical() {
        return null;
    }
    getMeta() {
        return super.getMeta(SEO.meta.badRoute);
    }
    getOpenGraphImages() {
        return [];
    }
}

// sportName - localized params
export class SportHeadMetaFactory extends BaseHeadMetaFactory {
    setParameters(context) {
        super.setParameters(context);
        const {sportId, type} = context.data.params;
        this.subCategory = undefined;
        this.sportCategory = this.#getSportCategory(sportId);
        this.isLeaguePage = !!type;
    }

    #getSportCategory(sportId) {
        const competitionsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportCompetitions(sportId));
        if (location.pathname.startsWith(competitionsPath)) {
            return 'COMPETITIONS';
        }
        const outrightsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportOutright(sportId));
        if (location.pathname.startsWith(outrightsPath)) {
            return 'OUTRIGHTS';
        }
        const matchesPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportMatches(sportId));
        if (location.pathname.startsWith(matchesPath)) {
            const sportCategory = 'MATCHES';
            const lastURLSegment = location.pathname.split('/').pop().toUpperCase();

            // update subCategory with proper filter if available ex. today,inplay,streaming
            if (sportCategory !== lastURLSegment) {
                this.subCategory = lastURLSegment;
            }
            return sportCategory;
        }
        const meetingsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportMeetings(sportId));
        if (location.pathname.startsWith(meetingsPath)) {
            const sportCategory = 'MEETINGS';
            const lastURLSegment = location.pathname.split('/').pop().toUpperCase();

            // update subCategory with proper filter if available ex. today,inplay,streaming
            if (sportCategory !== lastURLSegment) {
                this.subCategory = lastURLSegment;
            }
            return sportCategory;
        }

        const highlightsPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportHighlights(sportId));
        if (location.pathname.startsWith(highlightsPath)) {
            return 'HIGHLIGHTS';
        }

        const futurePath = this.routeResolver.getRoute(NavigationPathBuilder.toSportFuture(sportId));
        if (location.pathname.startsWith(futurePath)) {
            return 'FUTURE';
        }

        const priceBoostPath = this.routeResolver.getRoute(NavigationPathBuilder.toSportPriceBoosts(sportId));
        if (location.pathname.startsWith(priceBoostPath)) {
            return 'PRICE_BOOSTS';
        }
    }

    getTranslation(data, part) {
        const phrase = this.#getPhrase(data, part);

        if (phrase) {
            return data.localizedParams ? formatTranslationString(phrase, ...data.localizedParams.values()) : phrase;
        }

        // fallback to moj default
        const result = StringUtils.resolveString(data[part], this.l10n, true);
        return data.localizedParams ? formatTranslationString(result, ...data.localizedParams.values()) : result;
    }

    #getPhrase(data, part) {
        let phrase;
        const {sportId} = data.params;
        const sportSpecific = {
            sportId,
            sportCategory: this.sportCategory,
            subCategory: this.subCategory,
        };
        const tabSpecific = {
            sportCategory: this.sportCategory,
            subCategory: this.subCategory,
        };
        // prioritize sportSpecific keys over tabSpecific
        const categories = [sportSpecific, tabSpecific];
        const prefix = '$DBX.SPORT_PAGE';
        const suffix = `PAGE_${part.toUpperCase()}`;

        for (let i = 0; i < categories.length; i++) {
            // create array of object values and remove falsy values
            const currentCategory = Object.values(categories[i]).filter(x => x);
            // iterate through relevant key parts to find first match in dictionary
            // parts example: sportId:soccer sportCategory:MATCHES subCategory:TODAY
            currentCategory.find((_, index, keyParts) => {
                const lookUpKey = [prefix, ...keyParts.slice(0, keyParts.length - index), suffix].join('.');
                const str = StringUtils.resolveString(lookUpKey, this.l10n, true);
                if (str) {
                    phrase = str;
                    return true;
                }
            });
            if (phrase) break;
        }
        return phrase;
    }

    getMeta() {
        if (this.sportCategory === 'OUTRIGHTS' && this.isLeaguePage) {
            return super.getMeta(SEO.meta.outrightLeagues);
        }
        return super.getMeta(SEO.meta.sports);
    }
}

export class EventHeadMetaFactory extends BaseHeadMetaFactory {
    getKey(data, part) {
        const {sportId, eventId} = data.params;
        const inplayEventPath = this.routeResolver.getRoute(NavigationPathBuilder.toInplayEvent(eventId));

        if (location.pathname.startsWith(inplayEventPath)) {
            const sportKey = `$DBX.EVENT_PAGE.${sportId}.PAGE_${part.toUpperCase()}.INPLAY`;

            if (StringUtils.resolveString(sportKey, this.l10n, true)) {
                return sportKey;
            }

            return `$DBX.EVENT_PAGE.PAGE_${part.toUpperCase()}.INPLAY`;
        }

        return null;
    }

    getMeta() {
        return super.getMeta(SEO.meta.events);
    }
}

export class RaceMeetingHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.races);
    }
}

export class VirtualsHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.virtuals);
    }
}

export class InplayHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.inplay);
    }
    getKey(data, part) {
        const {sportId} = data.params;
        if (!sportId) {
            // No sportId meaning root (ALL) page
            return null; // no specific translation key for root page, so use default
        }
        return `$DBX.IN_PLAY_PAGE.${sportId.toUpperCase()}.PAGE_${part.toUpperCase()}`;
    }
}

export class SportCouponHeadMetaFactory extends BaseHeadMetaFactory {
    getKey(data, part) {
        const {sportId, couponId} = data.params;
        return `$DBX.SPORT_COUPON.${couponId.toUpperCase()}.${sportId.toUpperCase()}.PAGE_${part.toUpperCase()}`;
    }
    getMeta() {
        return super.getMeta(SEO.meta.coupons);
    }
}

export class EmptyContentHeadMetaFactory extends BaseHeadMetaFactory {
    getMeta() {
        return super.getMeta(SEO.meta.emptyContent);
    }
}

export class SingleLeagueHeadMetaFactory extends BaseHeadMetaFactory {
    getKey(data, part) {
        const {sportId, pageId} = data.params;
        const sportKey = `$DBX.SINGLE_LEAGUE_COUPON.${pageId.toUpperCase()}.${sportId}.PAGE_${part.toUpperCase()}`;

        if (StringUtils.resolveString(sportKey, this.l10n, true)) {
            return sportKey;
        }

        return `$DBX.SINGLE_LEAGUE_COUPON.${pageId.toUpperCase()}.PAGE_${part.toUpperCase()}`;
    }
}

// Do not generate anything.
export class MyAccountHeadMetaFactory extends BaseHeadMetaFactory {
    getCanonical() {
        return null;
    }
    getRelativeCanonical() {
        return null;
    }
    getTranslation() {
        return '';
    }
    getMeta() {
        return super.getMeta(SEO.meta.myAccount);
    }
    getOpenGraphImages() {
        return [];
    }
}

// Generic factory for lotto-related pages. Could be split into more factories once we have any specific SEO requirements
export class LottoPageHeadMetaFactory extends BaseHeadMetaFactory {}
